import 'bootstrap-icons/font/bootstrap-icons.css';
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./dashboard.css";

import AuthModal from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import PageSkeleton from '../components/skeletons/PageSkeleton';
import AdminHomeSummary from '../components/AdminHomeSummary';
import CustomerHomeSummary from '../components/CustomerHomeSummary';
import SalesHomeSummary from '../components/SalesHomeSummary';
import FinanceHomeSummary from '../components/FinanceHomeSummary';
import OperationsHomeSummary from '../components/OperationsHomeSummary';

const SearchSection = React.lazy(() => import("../components/dashboard/SearchSection"));
const Partner = React.lazy(() => import("../components/dashboard/Partner"));
const FeaturesSection = React.lazy(() => import("../components/dashboard/FeaturesSection"));
const RelatedTenders = React.lazy(() => import("../components/dashboard/RelatedTenders"));
const OrganizationCarousel = React.lazy(() => import("../components/dashboard/OrganizationCarousel"));
const MinistryCarousel = React.lazy(() => import("../components/dashboard/MinistryCarousel"));
const StateCarousel = React.lazy(() => import("../components/dashboard/StateCarousel"));
const CtaBand = React.lazy(() => import("../components/dashboard/CtaBand"));
const Blog = React.lazy(() => import("../components/dashboard/Blog"));
const ContactSection = React.lazy(() => import("../components/dashboard/ContactSection"));

const Dashboard = ({ showAuthModal, setShowAuthModal }) => {
  const location = useLocation();
  const [loadRest, setLoadRest] = useState(false);
  const restRef = useRef(null);
  const { role } = useAuth();

  // ✅ Auto-open modal when coming from "Create an account" link
  useEffect(() => {
    // Check if we should open the modal
    if (location.state?.openAuthModal) {
      // Set a small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        setShowAuthModal(true);
        // Clear the state after opening modal to prevent it from reopening on refresh
        window.history.replaceState({}, document.title);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [location, setShowAuthModal]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setLoadRest(true),
      { rootMargin: "200px" }
    );
    if (restRef.current) obs.observe(restRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    import("../pages/ActiveTenders");
    import("../pages/Results");
    import("../pages/Profile");
    import("../pages/AllMinistries");
  }, []);

  // Admin, Customer, Sales, Operations, and Finance users see ONLY their respective analytics summary on Home
  if (role === "admin" || role === "customer" || role === "sales" || role === "finance" || role === "operations") {
    return (
      <>
        <div
          style={{
            filter: showAuthModal ? "blur(4px)" : "none",
            pointerEvents: showAuthModal ? "none" : "auto",
            transition: "0.3s ease"
          }}
        >
          {role === "admin" ? <AdminHomeSummary /> 
            : role === "customer" ? <CustomerHomeSummary /> 
            : role === "sales" ? <SalesHomeSummary /> 
            : role === "operations" ? <OperationsHomeSummary />
            : <FinanceHomeSummary />}
        </div>

        <AuthModal
          show={showAuthModal}
          onHide={() => setShowAuthModal(false)}
          isLoginMode={false}
        />
      </>
    );
  }

  return (
    <>
      <div
        style={{
          filter: showAuthModal ? "blur(4px)" : "none",
          pointerEvents: showAuthModal ? "none" : "auto",
          transition: "0.3s ease"
        }}
      >
        <section className="hero-section">
          <img
            src="/images/hero-bg.webp"
            alt=""
            width="1920"
            height="700"
            loading="eager"
            decoding="async"
            className="hero-bg-img"
          />
        </section>

        <Suspense fallback={null}>
          <SearchSection />
        </Suspense>

        <Partner />

        <div ref={restRef}></div>

        {loadRest && (
          <Suspense
            fallback={
              <div className="text-center py-5">
                <PageSkeleton height="220px" />
                <PageSkeleton height="220px" />
                <PageSkeleton height="220px" />
              </div>
            }
          >
            <FeaturesSection />

            {role !== "operations" && <CtaBand setShowAuthModal={setShowAuthModal} />}
            <Blog />
            {role !== "operations" && role !== "customer" && <ContactSection />}
          </Suspense>
        )}
      </div>

      <AuthModal
        show={showAuthModal}
        onHide={() => setShowAuthModal(false)}
        isLoginMode={false}
      />
    </>
  );
};

export default Dashboard;